import React, { useCallback, useEffect, useRef } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useAudio } from '../../contexts/AudioContext';
import { VolumeHighIcon, StopIcon } from 'hugeicons-react';
import '../../Styles/Settings/VoicesSection.css';

/** Samples play through the shared TTS service under this id prefix, so they stop message audio and vice versa. */
const SAMPLE_ID_PREFIX = 'voice-sample:';
const sampleId = (voiceId: string) => `${SAMPLE_ID_PREFIX}${voiceId}`;

export const VoicesSection: React.FC = () => {
  const { voices } = useAuth();
  const { activeVoice, validateAndSetVoice } = useSettings();
  const { speak, stopAudio, currentAudioMessageId, isMessageConverting, isPlaying, isPaused } = useAudio();

  const sampleText = "Hello, I am Buddy, how can I help you today?";

  const isSamplePlaying = currentAudioMessageId?.startsWith(SAMPLE_ID_PREFIX) ?? false;
  const isSamplePlayingRef = useRef(isSamplePlaying);
  isSamplePlayingRef.current = isSamplePlaying;

  // Ensure a voice is selected when voices are available
  useEffect(() => {
    if (voices.length > 0 && !activeVoice) {
      const defaultVoice =
        voices.find(v => v.isDefault) ||
        voices[0];
      validateAndSetVoice(defaultVoice.id);
    }
  }, [voices, activeVoice, validateAndSetVoice]);

  // Stop a sample when leaving the section, but leave message audio alone
  useEffect(() => {
    return () => {
      if (isSamplePlayingRef.current) {
        stopAudio();
      }
    };
  }, [stopAudio]);

  const handleVoiceSelect = useCallback((voiceId: string) => {
    if (voiceId === activeVoice) return;

    // Stop any playing sample when switching voices
    if (isSamplePlaying) {
      stopAudio();
    }

    // Use validation function to ensure voice exists
    validateAndSetVoice(voiceId);
  }, [activeVoice, validateAndSetVoice, isSamplePlaying, stopAudio]);

  // Called straight from the click so the service can unlock audio playback.
  // Starting a sample replaces whatever was playing; errors are toasted by the audio provider.
  const handlePlaySample = useCallback((voiceId: string) => {
    void speak(sampleText, sampleId(voiceId), voiceId);
  }, [speak]);

  return (
    <div className="settings-item">
      <div className="settings-item-header">
        <div className="settings-item-header-left">
          <VolumeHighIcon />
          <h3>Text-to-Speech Voice</h3>
        </div>
      </div>
      <p className="setting-description">
        Choose your preferred voice for text-to-speech functionality
      </p>

      {voices.length === 0 ? (
        <div className="voices-empty">
          <p>No voices available</p>
        </div>
      ) : (
        voices.map((voice) => {
              const isThisSample = currentAudioMessageId === sampleId(voice.id);
              const isVoicePlaying = isThisSample && isPlaying && !isPaused;
              const isVoiceLoading = isThisSample && isMessageConverting;
              
              return (
                <div key={voice.name} className="settings-row horizontal">
                  <div>
                    <label htmlFor={`voice-${voice.name}`} className="voice-label">
                      <span className="voice-name">{voice.name}</span>
                    </label>
                  </div>
                  <div className="voice-controls">
                    <input
                      type="radio"
                      id={`voice-${voice.name}`}
                      name="voice-selection"
                      checked={activeVoice === voice.id}
                      onChange={() => handleVoiceSelect(voice.id)}
                      className="voice-radio"
                      aria-describedby={`voice-${voice.name}-description`}
                    />
                    <span 
                      id={`voice-${voice.name}-description`} 
                      className="sr-only"
                    >
                      {voice.name} voice option
                    </span>
                    <button
                      onClick={() => {
                        if (isVoicePlaying) {
                          stopAudio();
                        } else {
                          handlePlaySample(voice.id);
                        }
                      }}
                      disabled={isVoiceLoading}
                      className="voice-sample-button"
                      aria-label={
                        isVoicePlaying
                          ? `Stop sample of ${voice.name} voice`
                          : `Play sample of ${voice.name} voice`
                      }
                      title={
                        isVoicePlaying
                          ? `Stop sample of ${voice.name} voice`
                          : `Play sample of ${voice.name} voice`
                      }
                      type="button"
                    >
                      {isVoiceLoading ? (
                        <div className="loading-spinner" aria-hidden="true" />
                      ) : isVoicePlaying ? (
                        <StopIcon aria-hidden="true" />
                      ) : (
                        <VolumeHighIcon aria-hidden="true" />
                      )}
                      <span className="sr-only">
                        {isVoiceLoading
                          ? `Loading ${voice.name} voice sample`
                          : isVoicePlaying
                          ? `Stop ${voice.name} voice sample`
                          : `Play ${voice.name} voice sample`
                        }
                      </span>
                    </button>
                  </div>
                </div>
              );
        })
      )}
    </div>
  );
};
